import React, { useEffect, useState, useRef } from 'react';
import { collection, getDocs, doc as firestoreDoc, getDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { doc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default function SalesByCashier() {
  const { user, companyId } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [cashierData, setCashierData] = useState([]);
  const [selectedReceiptList, setSelectedReceiptList] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const chartRef = useRef();
  const perPage = 10;
  const [loading, setLoading] = useState(true);
  const toNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };
  const round2 = (val) => Math.round(toNumber(val) * 100) / 100;
  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      if (!user || !companyId) return;
      const q = query(
        collection(db, 'companies', companyId, 'orders'),
        where('paymentStatus', '!=', 'Void'),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);
      const ordersData = snapshot.docs.map(orderDoc => ({ id: orderDoc.id, ...orderDoc.data() }));

      const from = fromDate ? new Date(fromDate) : new Date(0);
      const to = toDate ? new Date(toDate) : new Date();
      to.setHours(23, 59, 59, 999);

      const filteredOrders = ordersData.filter(order => {
        const orderDate = parseTimestamp(order.timestamp);
        if (!orderDate) return false;
        return orderDate >= from && orderDate <= to;
      });

      setOrders(filteredOrders);

      const cashierSnap = await getDoc(firestoreDoc(db, 'companies', companyId, 'companySettings', 'main'));
      const cashiers = (cashierSnap.exists() ? cashierSnap.data().cashiers : []) || [];
      const cashierMap = cashiers.reduce((acc, cur) => {
        acc[cur.id] = cur.name;
        return acc;
      }, { main: 'Main' });

      const grouped = filteredOrders.reduce((acc, order) => {
        const cid = order.cashierId || 'main';
        if (!acc[cid]) acc[cid] = { total: 0, receipts: [], name: cashierMap[cid] || 'Unknown' };
        acc[cid].total = round2(acc[cid].total + toNumber(order.total));
        acc[cid].receipts.push({
          receiptNumber: order.receiptNumber,
          timestamp: order.timestamp
        });
        return acc;
      }, {});

      const result = Object.entries(grouped).map(([id, data]) => ({
        id,
        name: data.name,
        total: data.total,
        receipts: data.receipts
      })).filter(c => c.total > 0);

      setCashierData(result);
      setLoading(false);
    };
    load();
  }, [user, fromDate, toDate]);

  const filteredData = cashierData.filter((cashier) => {
    return (
      cashier.id.toLowerCase().includes(search.toLowerCase()) ||
      cashier.name.toLowerCase().includes(search.toLowerCase())
    );
  });

  const paginated = filteredData.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.max(1, Math.ceil(filteredData.length / perPage));

  const chartData = {
    labels: filteredData.map(c => c.name),
    datasets: [{
      label: 'Total Sales (RM)',
      data: filteredData.map(c => Number(c.total)),
      backgroundColor: '#00e1d7'
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `RM ${ctx.raw.toFixed(2)}`
        }
      },
      title: {
        display: true,
        text: 'Sales by Cashier',
        color: 'white',
        font: { size: 18 }
      }
    },
    scales: {
      x: {
        ticks: { color: 'gray' },
        grid: { color: 'rgba(255,255,255,0.1)' }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: 'gray',
          callback: (v) => `RM ${v}`
        },
        grid: { color: 'rgba(255,255,255,0.1)' }
      }
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

  const exportToPDF = async () => {
    const pdfDoc = new jsPDF();

    const companySnap = await getDoc(firestoreDoc(db, 'users', user.uid, 'companyProfile', 'info'));
    const companyName = companySnap.exists() ? companySnap.data().companyName : 'Company';

    const title = `Sales By Cashier Report (${formatDate(fromDate)} - ${formatDate(toDate)})`;

    pdfDoc.setFontSize(14);
    pdfDoc.text(companyName, 105, 15, { align: 'center' });

    pdfDoc.setFontSize(10);
    pdfDoc.text(title, 105, 22, { align: 'center' });

    if (chartRef.current) {
      const chartCanvas = chartRef.current.canvas;
      const chartImg = chartCanvas.toDataURL('image/png', 1.0);
      pdfDoc.addImage(chartImg, 'PNG', 30, 30, 150, 70);
    }

    autoTable(pdfDoc, {
      head: [['Cashier ID', 'Name', 'Total Sales']],
      body: filteredData.map(c => [c.id, c.name, `RM ${c.total.toFixed(2)}`]),
      startY: 110,
    });

    pdfDoc.text('Powered by CashMate', 105, pdfDoc.internal.pageSize.height - 10, { align: 'center' });

    pdfDoc.save(`sales-by-cashier_${formatDate(fromDate)}_to_${formatDate(toDate)}.pdf`.replace(/\//g, '-'));
  };
  
    if (loading) {
      return (
        <div className="w-full flex flex-col justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
          <span className="text-white">Loading information...</span>
        </div>
      );
    }

  return (
    <div className="p-6 text-white">
      <div className="flex items-center mb-4 gap-2">
        <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-1 text-gray-300 hover:text-white">
          <span className="text-lg">←</span> Back
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-4">Sales by Cashier</h2>

      <div className="flex flex-wrap gap-4 mb-6">
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <span className="text-gray-400">to</span>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <input type="search" placeholder="Search by name or ID" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <button onClick={exportToPDF} className="bg-purple-600 px-4 py-2 rounded">Export to PDF</button>
      </div>

      <div className="rounded-lg p-4 mb-4 w-full max-w-6xl mx-auto text-white h-[400px]">
        <Bar ref={chartRef} data={chartData} options={chartOptions} />
      </div>

      <div className="bg-[#1a1a1a] rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="px-4 py-2">Cashier ID</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Total Sales</th>
              <th className="px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((c, i) => (
              <tr key={i} className="border-t border-gray-700">
                <td className="px-4 py-2">{c.id}</td>
                <td className="px-4 py-2">{c.name}</td>
                <td className="px-4 py-2 font-bold">RM {c.total.toFixed(2)}</td>
                <td className="px-4 py-2">
                  <button
                    onClick={() => setSelectedReceiptList(c.receipts)}
                    className="bg-purple-600 hover:bg-purple-700 px-3 py-1 rounded text-white text-sm"
                  >
                    View Receipts
                  </button>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr><td colSpan={4} className="text-center text-gray-400 py-6">No data found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center flex-wrap mt-6 gap-2">
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            className={`px-4 py-2 rounded ${currentPage === i + 1 ? 'bg-purple-600 text-white' : 'bg-[#2c2c2c] text-gray-300'}`}
            onClick={() => setCurrentPage(i + 1)}
          >{i + 1}</button>
        ))}
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
      </div>

      {selectedReceiptList.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#121212] text-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Receipt Numbers</h3>
            <ul className="max-h-[300px] overflow-y-auto space-y-2">
              {selectedReceiptList.map((r, i) => (
                <li key={i} className="bg-[#1f1f1f] px-4 py-2 rounded">
                  #{r.receiptNumber} - {formatDate(r.timestamp)}
                </li>
              ))}
            </ul>
            <button
              className="mt-4 w-full bg-purple-600 py-2 rounded"
              onClick={() => setSelectedReceiptList([])}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
